import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Check, ChevronDown, CircleAlert } from 'lucide-react';
import { cn } from './cn';

/** Max menu height (keeps in sync with the `max-h-64` = 16rem class on the list). */
const MENU_MAX_HEIGHT = 256;
/** Gap between the trigger and the floating menu. */
const MENU_GAP = 6;

interface MenuPosition {
  left: number;
  width: number;
  /** Set when the menu opens downward (anchored to the trigger's bottom edge). */
  top?: number;
  /** Set when the menu flips upward (anchored to the trigger's top edge). */
  bottom?: number;
  placement: 'top' | 'bottom';
}

export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectOption {
  value: string;
  label: React.ReactNode;
  /** Optional leading icon rendered before the label. */
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** Field label rendered above the trigger. */
  label?: React.ReactNode;
  /** Placeholder shown when no value is selected. */
  placeholder?: React.ReactNode;
  /** Error message (string) or error flag (boolean). Renders the danger (red) validation treatment. */
  error?: string | boolean;
  /** Helper text below the field (hidden when there is an error). */
  helperText?: React.ReactNode;
  disabled?: boolean;
  /** Stretch to fill the container width. Default true (matches MUI fullWidth usage). */
  fullWidth?: boolean;
  size?: SelectSize;
  required?: boolean;
  /** Class applied to the trigger element. */
  className?: string;
  /** Class applied to the outer wrapper (label + field + helper). */
  containerClassName?: string;
  id?: string;
  name?: string;
  'aria-label'?: string;
}

const fieldSizes: Record<SelectSize, string> = {
  sm: 'h-9 text-sm',
  md: 'h-11 text-sm',
  lg: 'h-12 text-base',
};

/**
 * Custom accessible listbox select — replaces MUI <Select> + <MenuItem> + <FormControl>.
 * Trigger mirrors <Input> styling; dropdown animates via motion. Fully keyboard
 * operable (Arrow/Home/End/Enter/Escape) with listbox/option ARIA roles. Closes on
 * outside click and on select.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      value,
      onChange,
      label,
      placeholder,
      error,
      helperText,
      disabled,
      fullWidth = true,
      size = 'md',
      required,
      className,
      containerClassName,
      id,
      name,
      'aria-label': ariaLabel,
    },
    ref,
  ) => {
    const reactId = useId();
    const fieldId = id ?? reactId;
    const listboxId = `${fieldId}-listbox`;
    const hasError = Boolean(error);
    const errorText = typeof error === 'string' ? error : undefined;

    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);

    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const optionRefs = useRef<Array<HTMLLIElement | null>>([]);

    useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement, []);

    const selectedIndex = useMemo(
      () => options.findIndex((o) => o.value === value),
      [options, value],
    );
    const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

    const firstEnabledIndex = useCallback(() => {
      const i = options.findIndex((o) => !o.disabled);
      return i;
    }, [options]);

    const lastEnabledIndex = useCallback(() => {
      for (let i = options.length - 1; i >= 0; i -= 1) {
        if (!options[i].disabled) return i;
      }
      return -1;
    }, [options]);

    const moveActive = useCallback(
      (dir: 1 | -1, from: number) => {
        const len = options.length;
        if (len === 0) return;
        let next = from;
        for (let step = 0; step < len; step += 1) {
          next = (next + dir + len) % len;
          if (!options[next].disabled) {
            setActiveIndex(next);
            return;
          }
        }
      },
      [options],
    );

    const openMenu = useCallback(() => {
      if (disabled) return;
      setOpen(true);
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstEnabledIndex());
    }, [disabled, selectedIndex, firstEnabledIndex]);

    const closeMenu = useCallback((focusTrigger = true) => {
      setOpen(false);
      setActiveIndex(-1);
      if (focusTrigger) triggerRef.current?.focus();
    }, []);

    const commit = useCallback(
      (index: number) => {
        const opt = options[index];
        if (!opt || opt.disabled) return;
        onChange(opt.value);
        closeMenu();
      },
      [options, onChange, closeMenu],
    );

    // The menu is portaled to <body> so it is never clipped by an ancestor's
    // `overflow-hidden` (e.g. a Dialog panel) — position it under the trigger via
    // fixed coordinates, flipping upward when there isn't room below.
    const updatePosition = useCallback(() => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const flipUp =
        spaceBelow < Math.min(MENU_MAX_HEIGHT, 220) && spaceAbove > spaceBelow;
      setMenuPos(
        flipUp
          ? {
              placement: 'top',
              left: rect.left,
              width: rect.width,
              bottom: window.innerHeight - rect.top + MENU_GAP,
            }
          : {
              placement: 'bottom',
              left: rect.left,
              width: rect.width,
              top: rect.bottom + MENU_GAP,
            },
      );
    }, []);

    // Measure before paint when opening, then keep in sync with scroll/resize.
    useLayoutEffect(() => {
      if (!open) return;
      updatePosition();
      const onScroll = (e: Event) => {
        // Ignore scrolling that happens inside the menu itself (the trigger has
        // not moved, so recomputing would be wasted work / cause jitter).
        if (listRef.current?.contains(e.target as Node)) return;
        updatePosition();
      };
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [open, updatePosition]);

    // Outside-click closes the menu. The menu lives in a portal (outside rootRef),
    // so clicks inside it must be treated as inside.
    useEffect(() => {
      if (!open) return;
      const handlePointer = (e: MouseEvent | TouchEvent) => {
        const target = e.target as Node;
        if (
          !rootRef.current?.contains(target) &&
          !listRef.current?.contains(target)
        ) {
          setOpen(false);
          setActiveIndex(-1);
        }
      };
      document.addEventListener('mousedown', handlePointer);
      document.addEventListener('touchstart', handlePointer);
      return () => {
        document.removeEventListener('mousedown', handlePointer);
        document.removeEventListener('touchstart', handlePointer);
      };
    }, [open]);

    // Keep the active option scrolled into view.
    useEffect(() => {
      if (!open || activeIndex < 0) return;
      optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }, [open, activeIndex]);

    const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          e.preventDefault();
          if (!open) {
            openMenu();
          } else {
            moveActive(e.key === 'ArrowDown' ? 1 : -1, activeIndex);
          }
          break;
        case 'Home':
          if (open) {
            e.preventDefault();
            setActiveIndex(firstEnabledIndex());
          }
          break;
        case 'End':
          if (open) {
            e.preventDefault();
            setActiveIndex(lastEnabledIndex());
          }
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (open && activeIndex >= 0) {
            commit(activeIndex);
          } else {
            openMenu();
          }
          break;
        case 'Escape':
          if (open) {
            e.preventDefault();
            e.stopPropagation();
            closeMenu();
          }
          break;
        case 'Tab':
          if (open) closeMenu(false);
          break;
        default:
          break;
      }
    };

    const activeId = activeIndex >= 0 ? `${fieldId}-opt-${activeIndex}` : undefined;

    return (
      <div
        ref={rootRef}
        className={cn(
          'relative flex flex-col gap-1.5',
          fullWidth && 'w-full',
          containerClassName,
        )}
      >
        {label && (
          <label
            id={`${fieldId}-label`}
            htmlFor={fieldId}
            className="text-sm font-medium text-content"
          >
            {label}
            {required && <span className="ml-0.5 text-primary">*</span>}
          </label>
        )}

        {name && <input type="hidden" name={name} value={value} />}

        <button
          ref={triggerRef}
          type="button"
          id={fieldId}
          disabled={disabled}
          onClick={() => (open ? closeMenu() : openMenu())}
          onKeyDown={onTriggerKeyDown}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={activeId}
          aria-invalid={hasError || undefined}
          aria-required={required || undefined}
          aria-label={ariaLabel}
          aria-labelledby={label ? `${fieldId}-label` : undefined}
          className={cn(
            'group flex w-full items-center gap-2 rounded-xl border bg-surface px-3.5 text-left transition-all duration-200',
            'focus-visible:outline-none',
            // Field-validation errors use the danger (red) token + icon (CONVENTIONS §5);
            // everything else stays brand orange. Persistent ring while invalid.
            hasError
              ? 'border-danger ring-2 ring-danger/25'
              : 'border-line hover:border-primary/40 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30',
            open && 'border-primary ring-2 ring-primary/30',
            disabled && 'cursor-not-allowed opacity-60',
            fieldSizes[size],
            className,
          )}
        >
          {selected?.icon && (
            <span className="flex shrink-0 items-center text-muted">{selected.icon}</span>
          )}
          <span
            className={cn(
              'min-w-0 flex-1 truncate',
              selected ? 'text-content' : 'text-muted',
            )}
          >
            {selected ? selected.label : placeholder}
          </span>
          {hasError && (
            <CircleAlert
              size={18}
              strokeWidth={2}
              aria-hidden
              className="shrink-0 text-danger"
            />
          )}
          <ChevronDown
            size={size === 'lg' ? 20 : 18}
            aria-hidden
            className={cn(
              'shrink-0 text-muted transition-transform duration-200',
              open && 'rotate-180 text-primary',
            )}
          />
        </button>

        {typeof document !== 'undefined' &&
          createPortal(
            <AnimatePresence>
              {open && menuPos && (
                <motion.ul
                  ref={listRef}
                  id={listboxId}
                  role="listbox"
                  aria-labelledby={label ? `${fieldId}-label` : undefined}
                  tabIndex={-1}
                  initial={{ opacity: 0, y: menuPos.placement === 'top' ? 4 : -4, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: menuPos.placement === 'top' ? 4 : -4, scale: 0.98 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  style={{
                    position: 'fixed',
                    left: menuPos.left,
                    width: menuPos.width,
                    top: menuPos.top,
                    bottom: menuPos.bottom,
                    zIndex: 1400,
                    transformOrigin: menuPos.placement === 'top' ? 'bottom' : 'top',
                  } as CSSProperties}
                  className="max-h-64 overflow-auto rounded-xl border border-line bg-surface p-1 shadow-pop"
                >
                  {options.map((opt, index) => {
                const isSelected = opt.value === value;
                const isActive = index === activeIndex;
                return (
                  <li
                    key={opt.value}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    id={`${fieldId}-opt-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled || undefined}
                    onClick={() => !opt.disabled && commit(index)}
                    onMouseEnter={() => !opt.disabled && setActiveIndex(index)}
                    className={cn(
                      'flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                      opt.disabled && 'cursor-not-allowed opacity-50',
                      !opt.disabled && isActive && 'bg-primary/10',
                      isSelected
                        ? 'bg-primary/15 font-medium text-primary'
                        : 'text-content',
                    )}
                  >
                    {opt.icon && (
                      <span
                        className={cn(
                          'flex shrink-0 items-center',
                          isSelected ? 'text-primary' : 'text-muted',
                        )}
                      >
                        {opt.icon}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                    {isSelected && (
                      <Check size={16} aria-hidden className="shrink-0 text-primary" />
                    )}
                  </li>
                );
              })}
                </motion.ul>
              )}
            </AnimatePresence>,
            document.body,
          )}

        {errorText ? (
          <p className="flex items-start gap-1.5 text-xs font-medium text-danger">
            <CircleAlert size={13} strokeWidth={2.5} className="mt-px shrink-0" aria-hidden="true" />
            <span>{errorText}</span>
          </p>
        ) : helperText ? (
          <p className="text-xs text-muted">{helperText}</p>
        ) : null}
      </div>
    );
  },
);

Select.displayName = 'Select';
